import { Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { paths } from '../../routes/paths';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import type { TransactionDetail } from './types';
import { safeTransactionText } from './types';

export function ReversalChainPanel({
  detail,
  language,
  permissions,
}: {
  readonly detail: TransactionDetail;
  readonly language: TimeLanguage;
  readonly permissions: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const reversal = detail.reversal;
  const reversalEntries = detail.commissionLedgerEntries.filter(
    (entry) => entry.entryType === 'REVERSAL' || entry.source.reversalId !== null,
  );
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" variant="h5">
        {t('transactions.detail.reversalChain')}
      </Typography>
      <Typography color="text.secondary" sx={{ my: 1 }}>
        {t('transactions.detail.reversalContractNote')}
      </Typography>
      <Stack component="ol" spacing={1} sx={{ m: 0, pl: 3 }}>
        <li>
          <Typography>{t('transactions.detail.originalTransaction', { id: detail.id })}</Typography>
        </li>
        {reversal === null ? (
          <li>
            <Typography>{t('transactions.detail.notReversed')}</Typography>
          </li>
        ) : (
          <li>
            <Typography>
              {reversal.partnerReversalId} · {reversal.reasonCode} ·{' '}
              {safeTransactionText(reversal.reason)} · {timestamp(reversal.createdAt, language)}
            </Typography>
          </li>
        )}
        {reversalEntries.map((entry) => (
          <li key={entry.id}>
            {permissions.has('COMMISSION_LEDGER_VIEW') ? (
              <MuiLink
                component={Link}
                to={`${paths.commissionLedger}/${encodeURIComponent(entry.id)}`}
              >
                {t('transactions.detail.ledgerEntry', { id: entry.id })}
              </MuiLink>
            ) : (
              <Typography>{t('transactions.detail.ledgerEntry', { id: entry.id })}</Typography>
            )}
          </li>
        ))}
      </Stack>
    </Paper>
  );
}

function timestamp(value: string, language: TimeLanguage) {
  const formatted = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${formatted.display} (${formatted.originalOffset})`;
}
